/**
 * SearchrContext.tsx - SearchrContext - React context for universal search
 * 
 * CHANGE HISTORY:
 * - 251021_0543 [d4517d3] danielbeer: feat: apply performance optimizations to all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { SearchrManager } from './SearchrManager';

const SearchrContext = createContext(null);

export const SearchrProvider = ({ children, options = {} }) => {
  const [searchrManager] = useState(() => new Manager({}));
  const searchrRef = useRef(searchrManager);
  const [, forceUpdate] = useState({});
  
  const searchr = searchrRef.current;
  
  // Subscribe to changes
  useEffect(() => {
    const unsubscribe = searchr.subscribe(() => {
      forceUpdate({});
    });
    
    return unsubscribe;
  }, [searchr]);
  
  // Create callbacks at top level (not inside useMemo)
  const search = useCallback((query, options) => searchr.search(query, options), [searchr]);
  const setContext = useCallback((view, dataset) => searchr.setContext(view, dataset), [searchr]);
  const registerAdapter = useCallback((name, adapter) => searchr.registerAdapter(name, adapter), [searchr]);
  const setFreshnessScore = useCallback((name, score) => searchr.setFreshnessScore(name, score), [searchr]);
  const getHistory = useCallback(() => searchr.history, [searchr]);
  const saveSearch = useCallback((name, query, opts) => searchr.saveSearch(name, query, opts), [searchr]);
  const getSavedSearches = useCallback(() => Array.from(searchr.savedSearches.entries()), [searchr]);
  const clearCache = useCallback(() => searchr.clearCache(), [searchr]);
  
  const value = useMemo(() => ({
    searchr,
    search,
    setContext,
    registerAdapter,
    setFreshnessScore,
    getHistory,
    saveSearch,
    getSavedSearches,
    clearCache,
    scopes: searchr.scopes
  }), [searchr, search, setContext, registerAdapter, setFreshnessScore, getHistory, saveSearch, getSavedSearches, clearCache]);
  
  return (
    <SearchrContext.Provider value={value}>
      {children}
    </SearchrContext.Provider>
  );
};

export const useSearchr = () => {
  const context = useContext(SearchrContext);
  
  if (!context) {
    throw new Error('useSearchr must be used within SearchrProvider');
  }
  
  return context;
};

/**
 * Hook for searching with state management
 */
export const useSearch = (initialQuery = '', initialOptions = {}) => {
  const { search } = useSearchr();
  const [query, setQuery] = useState(initialQuery);
  const [options, setOptions] = useState(initialOptions);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const executeSearch = useCallback(async (q = query, opts = options) => {
    if (!q || q.trim() === '') {
      setResults([]);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const searchResults = await search(q, opts);
      setResults(searchResults);
    } catch (err) {
      setError(err.message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [query, options, search]);
  
  // Auto-search when query changes (with debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query) {
        executeSearch();
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [query, executeSearch]);
  
  return {
    query,
    setQuery,
    options,
    setOptions,
    results,
    loading,
    error,
    search: executeSearch
  };
};

/**
 * Hook for multi-level search
 */
export const useMultiLevelSearch = () => {
  const { search, scopes } = useSearchr();
  const [currentScope, setCurrentScope] = useState(scopes.CURRENT_VIEW);
  const [results, setResults] = useState({
    [scopes.CURRENT_VIEW]: [],
    [scopes.CURRENT_DATASET]: [],
    [scopes.ALL_DATASETS]: [],
    [scopes.EXTERNAL]: []
  });
  const [loading, setLoading] = useState({});
  
  const searchScope = useCallback(async (query, scope, options = {}) => {
    setLoading(prev => ({ ...prev, [scope]: true }));
    
    try {
      const scopeResults = await search(query, { ...options, scope });
      setResults(prev => ({ ...prev, [scope]: scopeResults }));
      return scopeResults;
    } catch (error) {
      console.error(`Search failed for scope ${scope}:`, error);
      return [];
    } finally {
      setLoading(prev => ({ ...prev, [scope]: false }));
    }
  }, [search]);
  
  const searchAll = useCallback(async (query, options = {}) => {
    const promises = Object.values(scopes).map(scope =>
      searchScope(query, scope, options)
    );
    
    await Promise.all(promises);
  }, [scopes, searchScope]);
  
  return {
    currentScope,
    setCurrentScope,
    results,
    loading,
    searchScope,
    searchAll,
    scopes
  };
};

/**
 * Hook for search suggestions/autocomplete
 */
export const useSearchSuggestions = (query, options = {}) => {
  const { search } = useSearchr();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }
    
    const timer = setTimeout(async () => {
      setLoading(true);
      
      try {
        const results = await search(query, {
          ...options,
          maxResults: 5
        });
        
        setSuggestions(results.map(r => r.name || r.title));
      } catch (error) {
        console.error('Suggestion search failed:', error);
      } finally {
        setLoading(false);
      }
    }, 200);
    
    return () => clearTimeout(timer);
  }, [query, search, options]);
  
  return { suggestions, loading };
};

export default SearchrContext;
